import React, { useEffect, useMemo, useRef, useState } from 'react';

// Sanitiza as opções para { id, label } como strings, descartando entradas incompletas.
function normalizeOptions(options) {
  if (!Array.isArray(options)) return [];

  return options
    .map((item) => ({
      id: String(item?.id ?? '').trim(),
      label: String(item?.label ?? '').trim(),
    }))
    .filter((item) => item.id && item.label);
}

// Select com busca para listas longas (ex.: cidades): filtra por texto e emite o id via onChange.
function SearchAutocompleteSelect({
  id,
  value,
  options,
  onChange,
  placeholder = 'Buscar...',
  disabled = false,
  emptyMessage = 'Nenhuma opção encontrada.',
}) {
  const rootRef = useRef(null);
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const selected = normalizedOptions.find((item) => String(item.id) === String(value));
    setInputValue(selected ? selected.label : '');
  }, [normalizedOptions, value]);

  // Fecha o dropdown ao clicar fora do componente.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Opções filtradas pelo texto digitado (limitadas a 100 para performance).
  const filteredOptions = useMemo(() => {
    const term = inputValue.trim().toLowerCase();
    if (!term) return normalizedOptions.slice(0, 100);

    return normalizedOptions
      .filter((item) => item.label.toLowerCase().includes(term))
      .slice(0, 100);
  }, [inputValue, normalizedOptions]);

  // Emite a mudança no formato de evento esperado pelos handlers das páginas ({ target: { value } }).
  const emitChange = (nextValue) => {
    if (typeof onChange === 'function') {
      onChange({ target: { value: nextValue } });
    }
  };

  // Atualiza o texto digitado, abre o dropdown e limpa a seleção quando o campo fica vazio.
  const handleInputChange = (event) => {
    const next = event.target.value;
    setInputValue(next);
    setIsOpen(true);

    if (!next.trim()) {
      emitChange('');
    }
  };

  // Seleciona uma opção: fixa o label, fecha o dropdown e emite o id.
  const handleSelect = (option) => {
    setInputValue(option.label);
    setIsOpen(false);
    emitChange(option.id);
  };

  return (
    <div ref={rootRef} className="relative">
      <input
        id={id}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />

      {isOpen ? (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">{emptyMessage}</div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(option)}
                className="block w-full px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-100"
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export default SearchAutocompleteSelect;